import React from 'react';
import { AlertTriangle, EyeOff, Clock } from 'lucide-react';
import { formatCentsRounded } from './_money';

/*
 * FinancePanel — revenue, A/R and deposits on the CEO dashboard (P142 PR B).
 *
 * Presentational only. Renders exactly what reports:finance:summary returned and
 * derives no figure of its own — a number computed here could disagree with the
 * same number computed on the server, and the one Carlos acts on would be
 * whichever happened to render.
 *
 * ⚠⚠ BOOKED AND COLLECTED ARE BOTH SHOWN, BOTH LABELLED, AND NEVER SUBSTITUTED.
 * They answer different questions — what was SOLD versus what was BANKED — and
 * on 28 Oct they differed by roughly $150K. A dashboard showing one under a bare
 * "Revenue" heading teaches its reader a number that is not the one they think.
 *
 * ⚠ THREE STATES, as everywhere else: failed / not-tracked / measured. An empty
 * mirror is "not available yet", never $0.
 *
 * NO PATIENT NAMES. Every figure is a count or a cent total.
 */

// ⚠ The shared formatter, same as the drill-down. This panel was already
// correct — it took cents and divided — but a second private definition of the
// same idea is how the drill-down's bug was born, so it goes too.
const money = formatCentsRounded;

const AGE_LABEL = {
  not_yet_due: 'Not yet due',
  d1_30: '1–30 days',
  d31_60: '31–60 days',
  d61_90: '61–90 days',
  d90_plus: '90+ days',
};
/*
 * ⛔⛔ `not_yet_due` IS NOT IN THIS LIST ANY MORE (card P256, Carlos 3 Nov 2026:
 * "separate genuinely collectible balances from ... future therapy balances not
 * yet due"). It used to be the first column of a table headed "Overdue, by age",
 * so money that is not late at all was being read — and totalled by eye — as
 * delinquent. It now has its own line below, outside the overdue table.
 */
const AGE_ORDER = ['d1_30', 'd31_60', 'd61_90', 'd90_plus'];

function Figure({ label, sublabel, value, tone, testid, onOpen }) {
  /*
   * ⚠ A figure is a BUTTON only when it can actually be opened. Offering a
   * press the server would refuse — or that leads nowhere — is the mistake the
   * referral queue's Promote button already avoids, and #722's tile
   * clickability carries the same rule.
   */
  if (onOpen) {
    return (
      <button type="button" onClick={onOpen} data-testid={testid}
        style={{ ...S.fig, ...S.figBtn }}>
        <div style={S.figLabel}>{label}</div>
        <div style={{ ...S.figValue, color: tone || '#212529' }}>{value}</div>
        {sublabel && <div style={S.figSub}>{sublabel}</div>}
      </button>
    );
  }
  return (
    <div style={S.fig} data-testid={testid}>
      <div style={S.figLabel}>{label}</div>
      <div style={{ ...S.figValue, color: tone || '#212529' }}>{value}</div>
      {sublabel && <div style={S.figSub}>{sublabel}</div>}
    </div>
  );
}

export default function FinancePanel({ data, failed, loading, canView, onOpenMetric }) {
  /*
   * ⚠ No permission ⇒ render NOTHING, not an empty panel. An empty finance
   * panel on a dashboard reads as "there is no money", which is worse than an
   * absent one. The server is the authoritative gate either way.
   */
  if (!canView) return null;

  if (failed) {
    return (
      <div data-testid="finance-failed" style={S.failed}>
        <AlertTriangle size={16} />
        <span style={{ marginLeft: 8 }}>
          Finance figures could not be loaded. These numbers are unknown right now, not zero.
        </span>
      </div>
    );
  }

  if (!data) {
    return <div data-testid="finance-loading" style={S.muted}>{loading ? 'Loading finance…' : null}</div>;
  }

  /* The mirror has never run. Not $0. */
  if (data.tracked === false) {
    return (
      <section data-testid="finance-not-tracked" style={S.wrap}>
        <header style={S.head}><h2 style={S.title}>Revenue</h2></header>
        <div style={S.notTracked} data-testid={`finance-reason-${data.reason || 'unknown'}`}>
          <EyeOff size={14} color="#6C757D" />
          <span style={{ marginLeft: 6 }}>{data.message || 'Payment data is not available yet.'}</span>
        </div>
        {/*
          * ⚠ "rows are present but no run finished" is an OPERATIONAL fact the
          * reader can act on — it means a run died part-way, not that there is
          * no data to mirror. Saying when data was last touched points whoever
          * reads it at the right question.
          */}
        {data.reason === 'no_successful_run' && data.mirroredAt && (
          <div style={S.muted} data-testid="finance-partial-detail">
            Data was last written {new Date(data.mirroredAt).toLocaleString()}, by a run that did not finish.
          </div>
        )}
      </section>
    );
  }

  const c = data.collected || {};
  /*
   * ⛔⛔ P161 item 1 — THE PANEL WAS READING THE ALL-TIME FIELDS.
   *
   * #744 netted the real partial refunds into `period.collected` and set
   * `provisional` to say whether any partly-refunded payment still has no
   * refund amount. This panel never moved: it kept reading `collected.cents`
   * (succeeded − FULL refunds only) and kept printing the "upper bound" caveat
   * unconditionally. MEASURED after the 29 Oct 18:10 UTC re-sync wrote 11
   * refund records: the screen still said $4,775,309 with the yellow warning,
   * over data where the netting was already exact.
   *
   * ⚠ THE ENVELOPE IS PREFERRED, NOT ASSUMED. A payload from a server that has
   * not deployed #744 carries no `period`, and falling back to the all-time
   * field is right there — the alternative is a blank figure during a rolling
   * deploy. `periodCollected` is what every conditional below keys on, so the
   * two shapes never half-mix.
   *
   * ⚠ unit 'cents' is ASSERTED, not assumed (P161 item 4). An envelope that
   * said dollars would be 100× off and perfectly plausible on this screen, so
   * a mismatch falls back rather than rendering.
   */
  const period = data.period || null;
  const periodCollected =
    period && period.collected && period.collected.tracked &&
    period.collected.unit === 'cents'
      ? period.collected
      : null;
  const collectedCents = periodCollected ? periodCollected.value : c.cents;
  const partialRefundCents =
    period && period.partialRefunds && period.partialRefunds.tracked
      ? period.partialRefunds.value
      : null;
  /*
   * ⛔ THE WARNING IS NOW TIED TO THE FLAG THAT MEASURES IT. `provisional` is
   * true only while some partly-refunded payment still has no refund amount.
   * When the envelope is absent we cannot know, so the OLD condition stands —
   * never silently dropping a caveat we cannot currently evaluate.
   */
  const collectedProvisional = periodCollected
    ? Boolean(periodCollected.provisional)
    : c.partiallyRefundedCount > 0;
  const o = data.overdue || {};
  const d = data.deposits || {};
  const aging = o.aging || {};

  return (
    <section data-testid="finance-panel" style={S.wrap}>
      <header style={S.head}>
        <h2 style={S.title}>Revenue &amp; receivables</h2>
        {/*
          * ⚠ WHEN, not just what. A figure from a mirror that stopped three days
          * ago is a historical fact presented as a current one, and the outage
          * is exactly when nobody thinks to ask.
          */}
        {/*
          * ⚠ The RUN's time, not the data's. An upsert that changed nothing
          * still stamps mirrored_at, so "when did the mirror last SUCCEED" is
          * the question a reader actually needs answered.
          */}
        {data.lastSuccessfulRunAt && (
          <span style={S.asOf} data-testid="finance-mirrored-at">
            <Clock size={12} /> last synced {new Date(data.lastSuccessfulRunAt).toLocaleString()}
            {data.lastRunMode === 'full_scan' ? ' (full scan)' : ''}
          </span>
        )}
      </header>

      <div style={S.row}>
        <Figure
          testid="finance-booked"
          onOpen={onOpenMetric && (() => onOpenMetric("finance.invoiced"))}
          label={data.booked.label}
          sublabel={data.booked.sublabel}
          value={money(data.booked.cents)}
        />
        <Figure
          testid="finance-collected"
          onOpen={onOpenMetric && (() => onOpenMetric("finance.collected"))}
          label={c.label}
          sublabel={periodCollected ? (data.range && data.range.label) || c.sublabel : c.sublabel}
          value={money(collectedCents)}
          tone="#1B6E3C"
        />
        <Figure
          testid="finance-overdue"
          onOpen={onOpenMetric && (() => onOpenMetric("finance.overdue"))}
          label={o.label}
          sublabel={o.sublabel}
          value={money(o.outstandingCents)}
          tone="#9B2C2C"
        />
        <Figure
          testid="finance-deposits"
          onOpen={onOpenMetric && (() => onOpenMetric("finance.deposits"))}
          label={d.label}
          sublabel={d.sublabel}
          value={money(d.receivedCents)}
        />
      </div>

      {/*
        * The netting shown, not hidden. "Collected" is a derived figure and a
        * reader is entitled to reconstruct it — including seeing what was
        * EXCLUDED, since failed payments are the half-million-dollar difference
        * between this number and a naive sum.
        */}
      <div style={S.detail} data-testid="finance-collected-breakdown">
        <strong>{c.label}</strong> = {money(c.succeededCents)} succeeded
        {' − '}{money(c.refundedCents)} refunded ({c.refundedCount} refund{c.refundedCount === 1 ? '' : 's'})
        {/* ⭐ The partial netting is SHOWN, because Collected no longer
            reconstructs without it — a reader who subtracts the two figures
            above and gets a different answer is right to distrust the card. */}
        {partialRefundCents !== null && partialRefundCents > 0 && (
          <span data-testid="finance-partial-refund-netted">
            {' − '}{money(partialRefundCents)} partly refunded
          </span>
        )}
        .{' '}
        <span style={S.excluded} data-testid="finance-failed-excluded">
          {money(c.failedCents)} in {c.failedCount} failed payment{c.failedCount === 1 ? '' : 's'} is excluded.
        </span>
        {c.processingCount > 0 && (
          <span data-testid="finance-processing">
            {' '}{money(c.processingCents)} still processing.
          </span>
        )}
        {/*
          * ⛔⛔ PARTIAL REFUNDS ARE NAMED, NOT NETTED, AND THE REASON IS SAID.
          *
          * A partly-refunded transaction's stored amount is the ORIGINAL charge;
          * GHL exposes no refunded-portion field this mirror keeps. Netting the
          * whole charge over-subtracts, netting nothing under-subtracts, and the
          * right number is not in the data. Saying so on screen is the only
          * honest option — a figure that quietly picked either would be wrong by
          * an amount nobody could see.
          */}
        {collectedProvisional && (
          <div style={S.caveat} data-testid="finance-partial-refunds">
            ⚠ {period && period.unresolvedPartialRefunds && period.unresolvedPartialRefunds.tracked
                 ? period.unresolvedPartialRefunds.value
                 : c.partiallyRefundedCount}{' '}
            partly-refunded payment{(period && period.unresolvedPartialRefunds && period.unresolvedPartialRefunds.tracked
                 ? period.unresolvedPartialRefunds.value
                 : c.partiallyRefundedCount) === 1 ? ' has' : 's have'} no refund amount in the mirrored
            data yet, so {(period && period.unresolvedPartialRefunds && period.unresolvedPartialRefunds.tracked
                 ? period.unresolvedPartialRefunds.value
                 : c.partiallyRefundedCount) === 1 ? 'it is' : 'they are'} <strong>not</strong> subtracted
            above — Collected is an upper bound by that amount. It clears on its own as the mirror fills.
          </div>
        )}
      </div>

      <div style={S.subhead}>Overdue — aged by due date</div>
      <table style={S.table} data-testid="finance-aging">
        <thead>
          <tr>{AGE_ORDER.map((k) => <th key={k} style={S.th}>{AGE_LABEL[k]}</th>)}</tr>
        </thead>
        <tbody>
          <tr>
            {AGE_ORDER.map((k) => (
              <td key={k} style={S.td} data-testid={`aging-${k}`}
                  onClick={onOpenMetric ? () => onOpenMetric('finance.overdue', { dimension: 'age', key: k }) : undefined}
                  role={onOpenMetric ? 'button' : undefined}
                  style={onOpenMetric ? { ...S.td, cursor: 'pointer' } : S.td}>
                {/*
                  * ⚠ THE ZERO IS SUPPLIED HERE, NOT BY THE FORMATTER. A band
                  * missing from the response means no invoices fall in it —
                  * a MEASURED zero, and $0 is the right thing to show. The
                  * formatter renders "—" for a non-finite input precisely so it
                  * never invents a zero of its own; that distinction only works
                  * if call sites that KNOW the value is zero say so.
                  */}
                <div style={S.agingMoney}>{money((aging[k] || { outstandingCents: 0 }).outstandingCents)}</div>
                <div style={S.agingCount}>{(aging[k] || {}).n || 0} invoice{((aging[k] || {}).n || 0) === 1 ? '' : 's'}</div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/*
        * ⚠ Owed, but not ageable. Surfaced rather than dropped: an invoice with
        * no due date still represents money, and leaving it out of both the
        * aging table and the page would make the columns silently fail to sum.
        */}
      {o.noDueDate && o.noDueDate.count > 0 && (
        <div style={S.muted} data-testid="finance-no-due-date">
          {money(o.noDueDate.outstandingCents)} outstanding across {o.noDueDate.count} invoice
          {o.noDueDate.count === 1 ? '' : 's'} with no due date — owed, but not ageable.
        </div>
      )}

      {/*
        * ⛔⛔ SHOWN APART, NEVER ADDED IN. These are the three classes Carlos
        * asked to have separated out of the $1,582,452 headline. Each is its own
        * line with its own click-through, and none of them is summed into
        * Overdue above — a reader can see what was set aside and why.
        */}
      {data.notYetDue && data.notYetDue.count > 0 && (
        <div style={S.muted} data-testid="finance-not-yet-due"
             onClick={onOpenMetric ? () => onOpenMetric('finance.overdue', { dimension: 'age', key: 'not_yet_due' }) : undefined}
             role={onOpenMetric ? 'button' : undefined}>
          <strong>{money(data.notYetDue.outstandingCents)}</strong> not yet due across{' '}
          {data.notYetDue.count} invoice{data.notYetDue.count === 1 ? '' : 's'} — issued, unpaid,
          still inside its due date. Not delinquent, and not counted above.
        </div>
      )}

      {data.draftsNotSent && data.draftsNotSent.count > 0 && (
        <div style={S.muted} data-testid="finance-drafts">
          <strong>{money(data.draftsNotSent.outstandingCents)}</strong> in{' '}
          {data.draftsNotSent.count} draft{data.draftsNotSent.count === 1 ? '' : 's'} never sent —
          not issued to anybody, and not counted above.
        </div>
      )}

      {data.suspectedDuplicates && data.suspectedDuplicates.groups > 0 && (
        <div style={S.muted} data-testid="finance-duplicates"
             onClick={onOpenMetric ? () => onOpenMetric('finance.overdue', { dimension: 'duplicates' }) : undefined}
             role={onOpenMetric ? 'button' : undefined}>
          <strong>{data.suspectedDuplicates.excessInvoices}</strong> suspected duplicate
          invoice{data.suspectedDuplicates.excessInvoices === 1 ? '' : 's'} in{' '}
          {data.suspectedDuplicates.groups} group{data.suspectedDuplicates.groups === 1 ? '' : 's'} —
          same bill-to, amount and issue date, worth{' '}
          {money(data.suspectedDuplicates.excessOutstandingCents)}.{' '}
          {/*
            * ⛔ FLAGGED, NOT SUBTRACTED. A repeat treatment at the same price on
            * the same day looks identical to a double-entry; which it is, is
            * Carlos's call. The figure stays in Overdue until he rules.
            */}
          Still included in Overdue — review before adjusting.
          {data.suspectedDuplicates.notCheckable && data.suspectedDuplicates.notCheckable.count > 0 && (
            <> {data.suspectedDuplicates.notCheckable.count} further invoice
            {data.suspectedDuplicates.notCheckable.count === 1 ? '' : 's'} could not be checked —
            no matched bill-to to group on.</>
          )}
        </div>
      )}

      {data.invoices && data.invoices.draftExcluded.count > 0 && (
        <div style={S.muted} data-testid="finance-drafts-excluded">
          {data.invoices.draftExcluded.count} draft invoice
          {data.invoices.draftExcluded.count === 1 ? '' : 's'} ({money(data.invoices.draftExcluded.cents)})
          {' '}excluded — a draft has not been issued to anyone.
        </div>
      )}

      {/*
        * A GHL status we do not recognise means our vocabulary has drifted from
        * theirs. Worth seeing the day it happens, not in a quarterly reconcile.
        */}
      {data.invoices && data.invoices.unknownStatusCount > 0 && (
        <div style={S.warn} data-testid="finance-unknown-status">
          <AlertTriangle size={13} />
          <span style={{ marginLeft: 6 }}>
            {data.invoices.unknownStatusCount} invoice
            {data.invoices.unknownStatusCount === 1 ? '' : 's'} carry a status we do not recognise and are
            counted in no bucket above.
          </span>
        </div>
      )}
    </section>
  );
}

const S = {
  wrap: { border: '1px solid #DEE2E6', background: '#fff', borderRadius: 12, padding: 16, marginBottom: 18 },
  head: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 },
  title: { margin: 0, fontSize: 15, fontWeight: 700, color: '#212529' },
  asOf: { fontSize: 11, color: '#ADB5BD', display: 'inline-flex', alignItems: 'center', gap: 4 },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  figBtn: { textAlign: 'left', cursor: 'pointer', font: 'inherit', width: '100%' },
  fig: { flex: '1 1 170px', minWidth: 170, border: '1px solid #F1F3F5', borderRadius: 10, padding: '11px 13px' },
  figLabel: { fontSize: 12, fontWeight: 700, color: '#495057' },
  figValue: { fontSize: 22, fontWeight: 700, margin: '3px 0 2px' },
  figSub: { fontSize: 11, color: '#868E96', lineHeight: 1.35 },
  detail: { marginTop: 12, fontSize: 12, color: '#495057', lineHeight: 1.5 },
  excluded: { color: '#868E96' },
  caveat: { marginTop: 8, fontSize: 11, color: '#92400E', background: '#FEF3C7', padding: '7px 9px', borderRadius: 7, lineHeight: 1.45 },
  subhead: { marginTop: 16, marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#495057' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '6px 10px', borderBottom: '2px solid #DEE2E6', color: '#495057', fontWeight: 700, fontSize: 11 },
  td: { padding: '8px 10px', borderBottom: '1px solid #F1F3F5', verticalAlign: 'top' },
  agingMoney: { fontWeight: 700, color: '#212529' },
  agingCount: { fontSize: 11, color: '#868E96' },
  muted: { fontSize: 11, color: '#868E96', marginTop: 8 },
  warn: { display: 'flex', alignItems: 'center', marginTop: 10, fontSize: 12, color: '#92400E', background: '#FEF3C7', padding: '8px 10px', borderRadius: 8 },
  notTracked: { display: 'flex', alignItems: 'center', fontSize: 13, color: '#6C757D' },
  failed: { display: 'flex', alignItems: 'center', padding: 12, borderRadius: 10, background: '#FDECEA', color: '#7B241C', fontSize: 13, marginBottom: 18 },
};
